import { KeyboardEvent, useEffect, useState, useRef } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { cn } from '@anytag/shared-utils';
import { useQueryHelper } from '@anytag/shared-hooks';
import { TextareaField } from '@anytag/shared-molecules';
import { localizedDateFormatter } from '@anytag/shared-utils';
import { AppLanguage, FormAction } from '@anytag/shared-types';
import { ButtonSubmit, Form, RenderDataWithFallback } from '@anytag/shared-atoms';
import {
  namedOperations,
  PromptPayload,
  useGetPromptLazyQuery,
  useGetPromptNamesQuery,
  useUpdatePromptMutation
} from '@anytag/graphql';

type FormContext = UseFormReturn<PromptPayload> & { values: PromptPayload };

export const Prompts = () => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { t, enqueueSnackbar, i18n } = useQueryHelper();
  const [selectedPrompt, setSelectedPrompt] = useState<string | null>(null);
  const { data: promptsData, loading: fetchingPrompts } = useGetPromptNamesQuery({ fetchPolicy: 'no-cache' });
  const {
    getGetPrompt,
    data: promptData,
    loading: fetchingPrompt
  } = useGetPromptLazyQuery({ fetchPolicy: 'no-cache' });
  const { callUpdatePrompt, loading: updatingPrompt } = useUpdatePromptMutation({
    refetchQueries: [namedOperations.Query.GetPrompt]
  });

  const prompts = promptsData?.getPromptNames?.promptNames;
  const prompt = promptData?.getPrompt;
  const firstPromptName = promptsData?.getPromptNames?.promptNames.at(0);

  // Initialize the first prompt
  useEffect(() => {
    if (firstPromptName && !selectedPrompt) {
      setSelectedPrompt(firstPromptName.promptName);
    }
  }, [firstPromptName, selectedPrompt]);

  // Fetch the selected prompt
  useEffect(() => {
    if (selectedPrompt) {
      getGetPrompt({ variables: { promptName: selectedPrompt } });
    }
  }, [selectedPrompt]);

  const handleSubmit: FormAction<PromptPayload>['onSubmit'] = async ({ promptName, content }) => {
    if (!selectedPrompt || !content) {
      return;
    }

    try {
      await callUpdatePrompt({ variables: { input: { promptName, content: content.trim() } } });
      enqueueSnackbar(t('Prompt updated successfully'), { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(t(error.message || 'failedToUpdate'), { variant: 'error' });
    }
  };

  // Remove whole variable when user press backspace or delete
  const handleKeyDown =
    ({ values: { variables }, setValue }: FormContext) =>
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (!variables.length) return;

      if (e.key === 'Backspace' || e.key === 'Delete') {
        const textarea = e.currentTarget;
        const cursorPosition = textarea.selectionStart;
        const text = textarea.value;

        // Check if cursor is at the end of a variable
        const variablePattern = new RegExp(`\\{\\{(${variables.join('|')})\\}\\}`, 'g');
        let match;

        while ((match = variablePattern.exec(text)) !== null) {
          const variableStart = match.index;
          const variableEnd = match.index + match[0].length;

          // If cursor is at the end of a variable, remove the entire variable
          if (cursorPosition === variableEnd) {
            e.preventDefault();
            const newText = text.slice(0, variableStart) + text.slice(variableEnd);
            setValue('content', newText);

            // Set cursor position after removing the variable
            setTimeout(() => {
              textarea.setSelectionRange(variableStart, variableStart);
            }, 0);
            return;
          }
        }
      }
    };

  const handleSelectVariable = (variable: string, { values, setValue, setFocus }: FormContext) => {
    const textarea = textareaRef.current;

    if (textarea) {
      const cursorPosition = textarea.selectionStart;
      const currentValue = values.content || '';
      const variableText = `{{${variable}}}`;
      // Insert the variable at the cursor position
      const newValue = currentValue.slice(0, cursorPosition) + variableText + currentValue.slice(cursorPosition);

      setValue('content', newValue, { shouldDirty: true });

      // Set cursor position after the inserted variable
      setTimeout(() => {
        const newCursorPosition = cursorPosition + variableText.length;
        textarea.setSelectionRange(newCursorPosition, newCursorPosition);
        setFocus('content');
      }, 0);
    }
  };

  return (
    <RenderDataWithFallback loading={fetchingPrompts || (!selectedPrompt && fetchingPrompt)}>
      <div className="mx-auto flex h-[calc(100vh-4rem-var(--footer-height))] min-h-111 w-full min-w-200 overflow-hidden rounded-sm bg-white shadow-container">
        <div className="box-border w-60 flex-none overflow-y-auto border-r-border py-4">
          <ul>
            {prompts?.map(({ promptName, displayName }) => (
              <li
                key={promptName}
                onClick={() => setSelectedPrompt(promptName)}
                className={cn('cursor-pointer p-4 text-sm hover:bg-light-gray', {
                  'bg-light-gray font-semibold': selectedPrompt === promptName
                })}
              >
                {displayName}
              </li>
            ))}
          </ul>
        </div>
        <div className="box-border flex-1 p-4">
          <RenderDataWithFallback
            hasNoData={!prompt}
            loading={fetchingPrompt}
            noDataNode={<div>{t('Prompt not found')}</div>}
          >
            {prompt ? (
              <Form.Provider<PromptPayload>
                onSubmit={handleSubmit}
                values={{ ...prompt }}
                className="flex h-full flex-col gap-2"
              >
                {(formContext) => {
                  const { values, formState } = formContext;

                  return (
                    <>
                      <TextareaField
                        name="content"
                        ref={textareaRef}
                        className="pt-0!"
                        disabled={updatingPrompt}
                        onKeyDown={handleKeyDown(formContext)}
                        classNames={{ base: 'h-full', textareaWrapper: 'h-full' }}
                      />

                      <div className="flex flex-none items-start gap-6 text-base">
                        <div className="box-border flex-none rounded-sm border border-border p-2 pb-1">
                          <p className="font-semibold">{t('Variables')}:</p>
                          {prompt.variables.length ? (
                            <ul className="max-h-60 min-w-60 overflow-y-auto">
                              {prompt.variables.map((variable) => (
                                <li
                                  key={variable}
                                  onClick={() => handleSelectVariable(variable, formContext)}
                                  className="my-2 cursor-pointer rounded-sm bg-dark-gray p-2 hover:opacity-80"
                                >{`{{${variable}}}`}</li>
                              ))}
                            </ul>
                          ) : (
                            <div>{t('No variables')}</div>
                          )}
                        </div>

                        <div className="flex flex-1 items-center justify-between gap-2 text-base">
                          <p>
                            <strong>{t('Last updated: ')}</strong>
                            {localizedDateFormatter(prompt?.lastUpdate || null, 'PPP', i18n.language as AppLanguage)}
                          </p>
                          <ButtonSubmit
                            title={t('Save')}
                            disabled={!formState.isDirty || !values.content}
                            loading={{ showIcon: true, status: updatingPrompt, title: t('Saving...') }}
                          />
                        </div>
                      </div>
                    </>
                  );
                }}
              </Form.Provider>
            ) : null}
          </RenderDataWithFallback>
        </div>
      </div>
    </RenderDataWithFallback>
  );
};
